/**
 * Push-уведомления (готово, но ВЫКЛЮЧЕНО флагом PUSH_ENABLED — см. docs/PUSH.md).
 *
 * Порядок:
 *  1. Разрешение спрашиваем ПОСЛЕ входа (не при запуске): человек уже понимает,
 *     что за приложение и зачем ему уведомления.
 *  2. Ключ устройства (FCM) регистрируем:
 *     а) в нашем Chatwoot — POST /api/v1/notification_subscriptions
 *        { notification_subscription: { subscription_type: 'fcm',
 *          subscription_attributes: { push_token, device_id, ... } } }
 *        (контроллер Api::V1::NotificationSubscriptionsController, строитель
 *        NotificationSubscriptionBuilder: одна запись на device_id, при входе
 *        другого человека запись переезжает к нему — как в штатном приложении
 *        chatwoot-mobile-app, settingsActions.saveDeviceDetails);
 *     б) в ленте уведомлений UNIA (ГиперТаск /api/notifications) — TODO ниже,
 *        у сервера пока нет приёма ключей устройств.
 *  3. Нажатие на уведомление → переход (lib/pushRoute.ts). Плагин придерживает
 *     нажатие, которым приложение было запущено, до первого слушателя — поэтому
 *     ставить слушатель после входа не поздно.
 *  4. Выход → ключ снимается с Chatwoot (DELETE с push_token), чтобы чужие
 *     уведомления не приходили на общий телефон.
 *
 * 🪤 На iOS плагин @capacitor/push-notifications отдаёт ключ APNs, а Chatwoot
 * шлёт через Firebase и ждёт ключ FCM. До включения на iOS нужна правка
 * AppDelegate (подмена ключа на FCM) — она расписана в docs/PUSH.md, шаг 6.
 */
import config from '@/lib/config';
import { isNative, platformName } from '@/lib/platform';
import { readPref, writePref } from '@/lib/storage';
import { pushToRoute } from '@/lib/pushRoute';
import { cwFetch } from '@/api/chatwoot';
import { APP_VERSION } from '@/lib/buildInfo';

const DEVICE_ID_KEY = 'saleus-device-id';
const PUSH_TOKEN_KEY = 'saleus-push-token';

export interface PushDeps {
  /** Переход внутри приложения (navigate из react-router). */
  navigate: (to: string) => void;
  /** Кабинет Chatwoot активного проекта — в уведомлении Chatwoot его нет. */
  getAccountId: () => number | null;
}

export function pushEnabled(): boolean {
  return config.PUSH_ENABLED && isNative();
}

/** Постоянный номер установки: по нему Chatwoot держит одну запись на устройство. */
async function deviceId(): Promise<string> {
  const saved = await readPref(DEVICE_ID_KEY);
  if (saved) return saved;
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  await writePref(DEVICE_ID_KEY, id);
  return id;
}

async function registerInChatwoot(token: string): Promise<void> {
  await cwFetch('/api/v1/notification_subscriptions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      notification_subscription: {
        subscription_type: 'fcm',
        subscription_attributes: {
          push_token: token,
          device_id: await deviceId(),
          devicePlatform: platformName(),
          deviceName: 'Saleus',
          buildNumber: APP_VERSION,
        },
      },
    }),
  });
}

/**
 * TODO(push-unia): регистрация ключа в ленте уведомлений UNIA (ГиперТаск
 * /api/notifications — CRM пишет туда же). Нужен приём ключей устройств на
 * сервере и рассылка через тот же проект Firebase; data уведомления — по
 * договорённости из lib/pushRoute.ts (route или deal_id).
 */
async function registerInUnia(_token: string): Promise<void> {
  // Намеренно пусто: серверной части ещё нет.
}

let started = false;
let cleanup: (() => void) | null = null;

/**
 * Включить уведомления для вошедшего человека. Повторные вызовы — без эффекта.
 * Возвращает «выключить слушатели» (на размонтировании).
 */
export async function startPush(deps: PushDeps): Promise<() => void> {
  if (!pushEnabled() || started) return () => {};
  started = true;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const handles = await Promise.all([
    PushNotifications.addListener('registration', ({ value }) => {
      void (async () => {
        await writePref(PUSH_TOKEN_KEY, value);
        try { await registerInChatwoot(value); } catch (e) { console.warn('[push] Chatwoot не принял ключ', e); }
        try { await registerInUnia(value); } catch (e) { console.warn('[push] UNIA не приняла ключ', e); }
      })();
    }),
    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] регистрация не удалась', err);
    }),
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const route = pushToRoute(notification.data as Record<string, unknown>, deps.getAccountId());
      if (route) deps.navigate(route);
    }),
  ]);
  cleanup = () => { handles.forEach((h) => void h.remove()); };

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') await PushNotifications.register();
  } catch (e) {
    console.warn('[push] разрешение/регистрация', e);
  }
  return () => stopPush();
}

function stopPush(): void {
  cleanup?.();
  cleanup = null;
  started = false;
}

/**
 * Перед выходом: снять ключ устройства с Chatwoot (пока пропуск Chatwoot ещё
 * жив) и отписаться от системы уведомлений. Ошибки глотаем — выход важнее.
 */
export async function forgetPushDevice(): Promise<void> {
  if (!pushEnabled()) return;
  const token = await readPref(PUSH_TOKEN_KEY);
  if (token) {
    try {
      await cwFetch(`/api/v1/notification_subscriptions?push_token=${encodeURIComponent(token)}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('[push] не удалось снять ключ с Chatwoot', e);
    }
    await writePref(PUSH_TOKEN_KEY, null);
  }
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.unregister();
  } catch { /* плагина нет — нечего снимать */ }
  stopPush();
}
